<script setup>
import {ref, computed, onMounted, onUnmounted} from 'vue'
import {logError, logInfo} from '../../plugins/logger.js'

const props = defineProps({
	imageData: {
		type: String,
		required: true
	},
	// Coins de départ en pourcentage {topLeft:{x,y}, topRight:{x,y}, bottomRight:{x,y}, bottomLeft:{x,y}}
	// Si fourni, la détection auto par luminance est sautée (on part des coins déjà détectés par jscanify).
	initialCorners: {
		type: Object,
		default: null
	}
})

const emit = defineEmits(['cropped', 'cancel'])

const containerRef = ref(null)
const imageRef = ref(null)
const canvasRef = ref(null)
const footerRef = ref(null)

const imageSize = ref({width: 0, height: 0})
const displaySize = ref({width: 0, height: 0})
const scale = ref(1)
const displayImageSrc = ref(null)

const corners = ref({
	topLeft: {x: 10, y: 10},
	topRight: {x: 90, y: 10},
	bottomRight: {x: 90, y: 90},
	bottomLeft: {x: 10, y: 90}
})

const activeCorner = ref(null)
const isDragging = ref(false)
const rotation = ref(0)

const magnifierCanvasRef = ref(null)
const magnifierPos = ref({x: 0, y: 0})
const MAGNIFIER_SIZE = 120
const MAGNIFIER_ZOOM = 2.5

// Réglages image : contraste (-30 à 60) et température (-50 froid à 50 chaud).
const contrast = ref(0)
const temperature = ref(0)

// Filtre appliqué à la fois à l'aperçu (<img style>) et au rendu final (ctx.filter),
// pour que ce qui est vu pendant l'ajustement corresponde exactement au résultat exporté.
const filterCss = computed(() => {
	const parts = [`contrast(${100 + contrast.value}%)`]
	if (temperature.value > 0) {
		parts.push(`sepia(${temperature.value * 0.6}%)`, `saturate(${100 + temperature.value * 0.3}%)`)
	} else if (temperature.value < 0) {
		parts.push(`hue-rotate(${temperature.value * 0.6}deg)`, `saturate(${100 + Math.abs(temperature.value) * 0.3}%)`)
	}
	return parts.join(' ')
})

const resetFilters = () => {
	contrast.value = 0
	temperature.value = 0
}

const runAutoEnhance = () => autoEnhance(imageRef.value)

// Panneau de réglages façon appareil photo (Pixel Camera) : bouton pour l'afficher,
// onglets pour choisir le réglage actif, règle graduée pour l'ajuster au doigt.
const showAdjustments = ref(false)
const activeAdjustment = ref('contrast')
const ADJUSTMENT_RANGES = {
	contrast: {min: -30, max: 60},
	temperature: {min: -50, max: 50}
}
const RULER_TICK_COUNT = 41
const rulerTicks = Array.from({length: RULER_TICK_COUNT}, (_, i) => ({major: i % 5 === 0}))

const activeRange = computed(() => ADJUSTMENT_RANGES[activeAdjustment.value])

const activeValue = computed({
	get() {
		return activeAdjustment.value === 'contrast' ? contrast.value : temperature.value
	},
	set(v) {
		if (activeAdjustment.value === 'contrast') contrast.value = v
		else temperature.value = v
	}
})

const thumbPercent = computed(() => {
	const {min, max} = activeRange.value
	return ((activeValue.value - min) / (max - min)) * 100
})

const rulerRef = ref(null)
const isDraggingRuler = ref(false)

const setValueFromClientX = (clientX) => {
	const el = rulerRef.value
	if (!el) return
	const rect = el.getBoundingClientRect()
	const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
	const {min, max} = activeRange.value
	activeValue.value = Math.round(min + pct * (max - min))
}

const handleRulerDown = (event) => {
	event.preventDefault()
	isDraggingRuler.value = true
	const clientX = event.touches ? event.touches[0].clientX : event.clientX
	setValueFromClientX(clientX)
}

const handleRulerMove = (event) => {
	if (!isDraggingRuler.value) return
	const clientX = event.touches ? event.touches[0].clientX : event.clientX
	setValueFromClientX(clientX)
}

const handleRulerUp = () => {
	isDraggingRuler.value = false
}

// Analyse la luminance et la balance des couleurs pour proposer un réglage automatique :
// image trop bleue → réchauffe, image plate (faible écart-type de luminance) → augmente le contraste.
// Prend l'objet Image chargé directement (pas le <img> affiché) pour ne pas dépendre du rendu DOM.
const autoEnhance = (img) => {
	if (!img || !img.width) return

	const canvas = document.createElement('canvas')
	const maxSize = 200
	const ratio = Math.min(maxSize / img.width, maxSize / img.height)
	canvas.width = img.width * ratio
	canvas.height = img.height * ratio

	const ctx = canvas.getContext('2d')
	ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

	try {
		const {data} = ctx.getImageData(0, 0, canvas.width, canvas.height)
		let sumR = 0, sumB = 0, sumLum = 0, sumLumSq = 0
		const n = data.length / 4
		for (let i = 0; i < data.length; i += 4) {
			const r = data[i], g = data[i + 1], b = data[i + 2]
			const lum = r * 0.299 + g * 0.587 + b * 0.114
			sumR += r
			sumB += b
			sumLum += lum
			sumLumSq += lum * lum
		}
		const avgR = sumR / n
		const avgB = sumB / n
		const avgLum = sumLum / n
		const variance = Math.max(0, sumLumSq / n - avgLum * avgLum)
		const stdDev = Math.sqrt(variance)

		const colorDiff = avgR - avgB
		temperature.value = Math.max(-50, Math.min(50, Math.round(-colorDiff * 0.8)))
		contrast.value = Math.max(0, Math.min(40, Math.round(45 - stdDev)))
	} catch (e) {
		// image cross-origin ou données illisibles : on garde les réglages actuels
	}
}

const getDisplayCoords = (point) => {
	if (!displaySize.value.width || !displaySize.value.height) {
		return {x: 0, y: 0}
	}
	return {
		x: (point.x / 100) * displaySize.value.width,
		y: (point.y / 100) * displaySize.value.height
	}
}

const getPercentCoords = (x, y) => {
	return {
		x: Math.max(0, Math.min(100, (x / displaySize.value.width) * 100)),
		y: Math.max(0, Math.min(100, (y / displaySize.value.height) * 100))
	}
}

const cornerPositions = computed(() => {
	return {
		topLeft: getDisplayCoords(corners.value.topLeft),
		topRight: getDisplayCoords(corners.value.topRight),
		bottomRight: getDisplayCoords(corners.value.bottomRight),
		bottomLeft: getDisplayCoords(corners.value.bottomLeft)
	}
})

const cropPath = computed(() => {
	if (!displaySize.value.width || !displaySize.value.height) {
		return 'M 0 0 Z'
	}
	const c = cornerPositions.value
	return `M ${c.topLeft.x} ${c.topLeft.y}
	        L ${c.topRight.x} ${c.topRight.y}
	        L ${c.bottomRight.x} ${c.bottomRight.y}
	        L ${c.bottomLeft.x} ${c.bottomLeft.y} Z`
})

const loadImage = () => {
	const img = new Image()
	img.onerror = (e) => {
		// Sans ce handler, l'image ne s'affiche jamais et l'écran de recadrage
		// reste vide : l'utilisateur croit que l'app est figée.
		logError('cropper.loadImage', 'image illisible', {kb: Math.round((props.imageData || '').length / 1024)})
		emit('cancel')
	}
	img.onload = () => {
		imageSize.value = {width: img.width, height: img.height}
		displayImageSrc.value = props.imageData
		rotation.value = 0
		calculateDisplaySize()
		if (props.initialCorners) {
			corners.value = props.initialCorners
		} else {
			autoDetectEdges(img)
		}
		autoEnhance(img)
		logInfo('cropper.loadImage', 'image chargée', {
			size: `${img.width}x${img.height}`,
			auto_corners: !!props.initialCorners,
		})
	}
	img.src = props.imageData
}

const calculateDisplaySize = () => {
	if (!containerRef.value || !imageSize.value.width || !imageSize.value.height) {
		return
	}

	const style = getComputedStyle(containerRef.value)
	const containerWidth = containerRef.value.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
	const containerHeight = containerRef.value.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom)

	if (!containerWidth || !containerHeight) {
		return
	}

	const imgRatio = imageSize.value.width / imageSize.value.height
	const containerRatio = containerWidth / containerHeight

	if (imgRatio > containerRatio) {
		displaySize.value.width = containerWidth
		displaySize.value.height = containerWidth / imgRatio
	} else {
		displaySize.value.height = containerHeight
		displaySize.value.width = containerHeight * imgRatio
	}

	scale.value = imageSize.value.width / displaySize.value.width
}

// Détection naïve des bords par seuillage de luminance (repère les zones claires = papier).
const autoDetectEdges = async (img) => {
	const canvas = document.createElement('canvas')
	const ctx = canvas.getContext('2d')

	const maxSize = 300
	const ratio = Math.min(maxSize / img.width, maxSize / img.height)
	canvas.width = img.width * ratio
	canvas.height = img.height * ratio

	ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

	try {
		const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
		const edges = detectEdges(imageData)

		if (edges) {
			corners.value = {
				topLeft: {
					x: (edges.topLeft.x / canvas.width) * 100,
					y: (edges.topLeft.y / canvas.height) * 100
				},
				topRight: {
					x: (edges.topRight.x / canvas.width) * 100,
					y: (edges.topRight.y / canvas.height) * 100
				},
				bottomRight: {
					x: (edges.bottomRight.x / canvas.width) * 100,
					y: (edges.bottomRight.y / canvas.height) * 100
				},
				bottomLeft: {
					x: (edges.bottomLeft.x / canvas.width) * 100,
					y: (edges.bottomLeft.y / canvas.height) * 100
				}
			}
		}
	} catch (e) {
		// on garde les coins par défaut
	}
}

const detectEdges = (imageData) => {
	const {width, height, data} = imageData

	const grayscale = new Uint8Array(width * height)
	for (let i = 0; i < data.length; i += 4) {
		grayscale[i / 4] = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114)
	}

	const threshold = 180
	let minX = width, maxX = 0, minY = height, maxY = 0
	let foundLight = false

	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			const idx = y * width + x
			if (grayscale[idx] > threshold) {
				foundLight = true
				if (x < minX) minX = x
				if (x > maxX) maxX = x
				if (y < minY) minY = y
				if (y > maxY) maxY = y
			}
		}
	}

	if (!foundLight || maxX - minX < width * 0.2 || maxY - minY < height * 0.2) {
		return null
	}

	const marginX = (maxX - minX) * 0.02
	const marginY = (maxY - minY) * 0.02

	return {
		topLeft: {x: Math.max(0, minX - marginX), y: Math.max(0, minY - marginY)},
		topRight: {x: Math.min(width, maxX + marginX), y: Math.max(0, minY - marginY)},
		bottomRight: {x: Math.min(width, maxX + marginX), y: Math.min(height, maxY + marginY)},
		bottomLeft: {x: Math.max(0, minX - marginX), y: Math.min(height, maxY + marginY)}
	}
}

const handlePointerDown = (corner, event) => {
	event.preventDefault()
	activeCorner.value = corner
	isDragging.value = true

	if (containerRef.value) {
		const rect = containerRef.value.getBoundingClientRect()
		const offsetX = (containerRef.value.clientWidth - displaySize.value.width) / 2
		const offsetY = (containerRef.value.clientHeight - displaySize.value.height) / 2
		const clientX = event.touches ? event.touches[0].clientX : event.clientX
		const clientY = event.touches ? event.touches[0].clientY : event.clientY
		magnifierPos.value = {x: clientX - rect.left, y: clientY - rect.top}
		drawMagnifier(clientX - rect.left - offsetX, clientY - rect.top - offsetY)
	}
}

const handlePointerMove = (event) => {
	if (!isDragging.value || !activeCorner.value || !containerRef.value) return

	const rect = containerRef.value.getBoundingClientRect()
	const offsetX = (containerRef.value.clientWidth - displaySize.value.width) / 2
	const offsetY = (containerRef.value.clientHeight - displaySize.value.height) / 2

	const clientX = event.touches ? event.touches[0].clientX : event.clientX
	const clientY = event.touches ? event.touches[0].clientY : event.clientY

	const x = clientX - rect.left - offsetX
	const y = clientY - rect.top - offsetY

	const coords = getPercentCoords(x, y)
	corners.value[activeCorner.value] = coords

	magnifierPos.value = {x: clientX - rect.left, y: clientY - rect.top}
	drawMagnifier(x, y)
}

const handlePointerUp = () => {
	activeCorner.value = null
	isDragging.value = false
}

const drawMagnifier = (displayX, displayY) => {
	const canvas = magnifierCanvasRef.value
	const img = imageRef.value
	if (!canvas || !img) return

	const size = MAGNIFIER_SIZE
	const dpr = window.devicePixelRatio || 1
	canvas.width = size * dpr
	canvas.height = size * dpr

	const ctx = canvas.getContext('2d')
	ctx.scale(dpr, dpr)

	ctx.beginPath()
	ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
	ctx.clip()

	ctx.fillStyle = '#1f2937'
	ctx.fillRect(0, 0, size, size)

	const srcCenterX = displayX * scale.value
	const srcCenterY = displayY * scale.value
	const srcRadius = (size / 2 / MAGNIFIER_ZOOM) * scale.value

	ctx.filter = filterCss.value
	ctx.drawImage(
		img,
		srcCenterX - srcRadius, srcCenterY - srcRadius,
		srcRadius * 2, srcRadius * 2,
		0, 0, size, size
	)

	ctx.strokeStyle = 'rgba(32, 128, 240, 0.8)'
	ctx.lineWidth = 1
	ctx.setLineDash([6, 4])
	ctx.beginPath()
	ctx.moveTo(size / 2, 0)
	ctx.lineTo(size / 2, size)
	ctx.moveTo(0, size / 2)
	ctx.lineTo(size, size / 2)
	ctx.stroke()
	ctx.setLineDash([])

	ctx.fillStyle = '#2080f0'
	ctx.beginPath()
	ctx.arc(size / 2, size / 2, 3, 0, Math.PI * 2)
	ctx.fill()
	ctx.strokeStyle = 'white'
	ctx.lineWidth = 1
	ctx.stroke()

	ctx.beginPath()
	ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2)
	ctx.strokeStyle = 'white'
	ctx.lineWidth = 3
	ctx.stroke()
}

const magnifierStyle = computed(() => {
	if (!isDragging.value || !activeCorner.value || !containerRef.value) return {}

	const cw = containerRef.value.clientWidth
	const ch = containerRef.value.clientHeight
	const x = magnifierPos.value.x
	const y = magnifierPos.value.y
	const margin = 16

	const touchInLeftHalf = x < cw / 2
	const touchInTopHalf = y < ch / 2

	const left = touchInLeftHalf ? cw - MAGNIFIER_SIZE - margin : margin
	const top = touchInTopHalf ? ch - MAGNIFIER_SIZE - margin : margin

	return {
		left: `${left}px`,
		top: `${top}px`,
	}
})

const rebuildDisplayImage = () => {
	const img = new Image()
	img.onerror = () => logError('cropper.rebuildDisplayImage', 'image illisible', {rotation: rotation.value})
	img.onload = () => {
		if (rotation.value === 0) {
			displayImageSrc.value = props.imageData
			imageSize.value = {width: img.width, height: img.height}
		} else {
			const offscreen = document.createElement('canvas')
			const isRotated = rotation.value === 90 || rotation.value === 270
			offscreen.width = isRotated ? img.height : img.width
			offscreen.height = isRotated ? img.width : img.height
			const ctx = offscreen.getContext('2d')
			ctx.translate(offscreen.width / 2, offscreen.height / 2)
			ctx.rotate((rotation.value * Math.PI) / 180)
			ctx.drawImage(img, -img.width / 2, -img.height / 2)
			displayImageSrc.value = offscreen.toDataURL('image/jpeg', 0.92)
			imageSize.value = {width: offscreen.width, height: offscreen.height}
		}
		calculateDisplaySize()
	}
	img.src = props.imageData
}

const rotateLeft = () => {
	rotation.value = (rotation.value + 270) % 360
	rebuildDisplayImage()
}
const rotateRight = () => {
	rotation.value = (rotation.value + 90) % 360
	rebuildDisplayImage()
}

const resetCorners = () => {
	corners.value = {
		topLeft: {x: 5, y: 5},
		topRight: {x: 95, y: 5},
		bottomRight: {x: 95, y: 95},
		bottomLeft: {x: 5, y: 95}
	}
}

// Au-delà de ce nombre de pixels, toDataURL rend un canvas vide sur certains
// mobiles (limite mémoire canvas, très basse sur les anciens iOS) : on réduit
// la sortie plutôt que de produire une image blanche.
const MAX_OUTPUT_PIXELS = 4_000_000

const applyCrop = async () => {
	const canvas = canvasRef.value
	if (!canvas) {
		logError('cropper.applyCrop', 'canvas absent', {})
		return
	}

	const img = new Image()
	img.onerror = () => {
		logError('cropper.applyCrop', 'image source illisible', {})
		alert("Le recadrage a échoué. Reprenez la photo.")
	}
	img.onload = () => {
		const rw = img.width
		const rh = img.height

		const srcCorners = [
			{x: (corners.value.topLeft.x / 100) * rw, y: (corners.value.topLeft.y / 100) * rh},
			{x: (corners.value.topRight.x / 100) * rw, y: (corners.value.topRight.y / 100) * rh},
			{x: (corners.value.bottomRight.x / 100) * rw, y: (corners.value.bottomRight.y / 100) * rh},
			{x: (corners.value.bottomLeft.x / 100) * rw, y: (corners.value.bottomLeft.y / 100) * rh}
		]

		const topWidth = Math.hypot(srcCorners[1].x - srcCorners[0].x, srcCorners[1].y - srcCorners[0].y)
		const bottomWidth = Math.hypot(srcCorners[2].x - srcCorners[3].x, srcCorners[2].y - srcCorners[3].y)
		const leftHeight = Math.hypot(srcCorners[3].x - srcCorners[0].x, srcCorners[3].y - srcCorners[0].y)
		const rightHeight = Math.hypot(srcCorners[2].x - srcCorners[1].x, srcCorners[2].y - srcCorners[1].y)
		let outputWidth = Math.round(Math.max(topWidth, bottomWidth))
		let outputHeight = Math.round(Math.max(leftHeight, rightHeight))

		if (!outputWidth || !outputHeight) {
			logError('cropper.applyCrop', 'dimensions de sortie nulles', {
				source: `${rw}x${rh}`, corners: JSON.stringify(corners.value),
			})
			alert("Le cadrage est invalide. Réajustez les coins.")
			return
		}

		const pixels = outputWidth * outputHeight
		if (pixels > MAX_OUTPUT_PIXELS) {
			const ratio = Math.sqrt(MAX_OUTPUT_PIXELS / pixels)
			outputWidth = Math.round(outputWidth * ratio)
			outputHeight = Math.round(outputHeight * ratio)
			logInfo('cropper.applyCrop', 'sortie réduite', {from: pixels, to: outputWidth * outputHeight})
		}

		try {
			canvas.width = outputWidth
			canvas.height = outputHeight

			const ctx = canvas.getContext('2d')
			perspectiveTransform(ctx, img, srcCorners, outputWidth, outputHeight)

			const croppedImage = canvas.toDataURL('image/jpeg', 0.92)
			if (!croppedImage || croppedImage.length < 1000) {
				throw new Error(`toDataURL vide (${outputWidth}x${outputHeight})`)
			}

			logInfo('cropper.applyCrop', 'recadrage effectué', {
				source: `${rw}x${rh}`,
				output: `${outputWidth}x${outputHeight}`,
				kb: Math.round(croppedImage.length / 1024),
			})
			emit('cropped', croppedImage)
		} catch (e) {
			logError('cropper.applyCrop', e, {source: `${rw}x${rh}`, output: `${outputWidth}x${outputHeight}`})
			alert("Le recadrage a échoué sur cet appareil. Réessayez avec une photo de plus près.")
		}
	}
	img.src = displayImageSrc.value
}

// Transformation de perspective : une bande rectangulaire tous les 4px en sortie.
// ctx.rect() découpe chaque bande sans espace ni recouvrement → pas de coutures visibles.
const perspectiveTransform = (ctx, img, srcCorners, width, height) => {
	const STRIP = 4
	ctx.imageSmoothingEnabled = true
	ctx.imageSmoothingQuality = 'high'
	ctx.filter = filterCss.value

	const getSrc = (u, v) => ({
		x: (1 - v) * ((1 - u) * srcCorners[0].x + u * srcCorners[1].x) + v * ((1 - u) * srcCorners[3].x + u * srcCorners[2].x),
		y: (1 - v) * ((1 - u) * srcCorners[0].y + u * srcCorners[1].y) + v * ((1 - u) * srcCorners[3].y + u * srcCorners[2].y)
	})

	for (let y = 0; y < height; y += STRIP) {
		const h = Math.min(STRIP, height - y)
		const v = (y + h / 2) / height

		const sL = getSrc(0, v)
		const sR = getSrc(1, v)

		const dx = sR.x - sL.x
		const dy = sR.y - sL.y
		const L = Math.hypot(dx, dy)
		if (L < 0.001) continue

		const cosA = dx / L
		const sinA = dy / L
		const scaleFactor = width / L

		const a = scaleFactor * cosA
		const c = scaleFactor * sinA
		const e = -scaleFactor * (cosA * sL.x + sinA * sL.y)
		const b = -sinA
		const d = cosA
		const f = sinA * sL.x - cosA * sL.y + y + h / 2

		ctx.save()
		ctx.beginPath()
		ctx.rect(0, y, width, h)
		ctx.clip()
		ctx.setTransform(a, b, c, d, e, f)
		ctx.drawImage(img, 0, 0)
		ctx.restore()
	}
}

// Vérifie que les boutons du bas sont bien dans la zone visible. Si le footer
// déborde, l'écran paraît figé : tout s'affiche mais rien n'est cliquable.
const checkFooterVisible = () => {
	if (!footerRef.value) return
	const rect = footerRef.value.getBoundingClientRect()
	const visibleHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight
	const context = {
		footer_bottom: Math.round(rect.bottom),
		footer_top: Math.round(rect.top),
		inner_height: window.innerHeight,
		visual_height: Math.round(visibleHeight),
		container_height: containerRef.value ? containerRef.value.clientHeight : null,
		display: `${Math.round(displaySize.value.width)}x${Math.round(displaySize.value.height)}`,
	}
	if (rect.bottom > visibleHeight + 1 || rect.top >= visibleHeight) {
		logError('cropper.layout', 'boutons hors écran', context)
	} else {
		logInfo('cropper.layout', 'recadrage affiché', context)
	}
}

// Le viewport bouge après la sortie du plein écran caméra, à la rotation et
// quand la barre d'outils du navigateur se replie : il faut refaire le calcul,
// sinon l'image garde une taille prévue pour un écran plus grand.
const handleViewportChange = () => {
	calculateDisplaySize()
	checkFooterVisible()
}

onMounted(() => {
	loadImage()
	// Après la première peinture, une fois le layout stabilisé.
	setTimeout(checkFooterVisible, 500)
	window.addEventListener('resize', calculateDisplaySize)
	window.addEventListener('orientationchange', handleViewportChange)
	document.addEventListener('fullscreenchange', handleViewportChange)
	document.addEventListener('webkitfullscreenchange', handleViewportChange)
	if (window.visualViewport) {
		window.visualViewport.addEventListener('resize', handleViewportChange)
	}
	document.addEventListener('mousemove', handlePointerMove)
	document.addEventListener('mouseup', handlePointerUp)
	document.addEventListener('touchmove', handlePointerMove, {passive: false})
	document.addEventListener('touchend', handlePointerUp)
	document.addEventListener('mousemove', handleRulerMove)
	document.addEventListener('mouseup', handleRulerUp)
	document.addEventListener('touchmove', handleRulerMove, {passive: false})
	document.addEventListener('touchend', handleRulerUp)
})

onUnmounted(() => {
	window.removeEventListener('resize', calculateDisplaySize)
	window.removeEventListener('orientationchange', handleViewportChange)
	document.removeEventListener('fullscreenchange', handleViewportChange)
	document.removeEventListener('webkitfullscreenchange', handleViewportChange)
	if (window.visualViewport) {
		window.visualViewport.removeEventListener('resize', handleViewportChange)
	}
	document.removeEventListener('mousemove', handlePointerMove)
	document.removeEventListener('mouseup', handlePointerUp)
	document.removeEventListener('touchmove', handlePointerMove)
	document.removeEventListener('touchend', handlePointerUp)
	document.removeEventListener('mousemove', handleRulerMove)
	document.removeEventListener('mouseup', handleRulerUp)
	document.removeEventListener('touchmove', handleRulerMove)
	document.removeEventListener('touchend', handleRulerUp)
})
</script>

<template>
	<div class="cropper-root">
		<div class="cropper-header">
			<h2>Ajuster le cadrage</h2>
			<div class="header-actions">
				<button
					@click="showAdjustments = !showAdjustments"
					class="icon-btn"
					:class="{active: showAdjustments}"
					title="Réglages de l'image">
					<i class="fas fa-sliders-h"></i>
				</button>
				<button @click="rotateLeft" class="icon-btn" title="Rotation gauche">
					<i class="fas fa-undo"></i>
				</button>
				<button @click="rotateRight" class="icon-btn" title="Rotation droite">
					<i class="fas fa-redo"></i>
				</button>
				<button @click="resetCorners" class="reset-btn">
					<i class="fas fa-sync-alt me-1"></i>
					Réinitialiser
				</button>
			</div>
		</div>

		<div class="cropper-instructions">
			<i class="fas fa-sliders-h me-1"></i>
			Déplacez les coins pour ajuster le cadrage du ticket
		</div>

		<div v-if="showAdjustments" class="adjust-panel">
			<div class="ruler-pill">
				<i class="fas ruler-icon-sm" :class="activeAdjustment === 'contrast' ? 'fa-circle-notch' : 'fa-snowflake'"></i>
				<div
					ref="rulerRef"
					class="ruler-track"
					@mousedown="handleRulerDown"
					@touchstart="handleRulerDown">
					<div class="ruler-ticks">
						<span v-for="(tick, i) in rulerTicks" :key="i" class="ruler-tick" :class="{major: tick.major}"></span>
					</div>
					<div class="ruler-thumb" :style="{left: thumbPercent + '%'}"></div>
				</div>
				<i class="fas ruler-icon-lg" :class="activeAdjustment === 'contrast' ? 'fa-adjust' : 'fa-fire'"></i>
			</div>

			<div class="adjust-tabs">
				<button class="adjust-tab" @click="resetFilters">
					<i class="fas fa-undo"></i>
					<span>Tout réinitialiser</span>
				</button>
				<div class="adjust-sep"></div>
				<button class="adjust-tab" :class="{active: activeAdjustment === 'contrast'}" @click="activeAdjustment = 'contrast'">
					<i class="fas fa-adjust"></i>
					<span>Contraste</span>
				</button>
				<button class="adjust-tab" :class="{active: activeAdjustment === 'temperature'}" @click="activeAdjustment = 'temperature'">
					<i class="fas fa-thermometer-half"></i>
					<span>Température</span>
				</button>
				<button class="adjust-tab" @click="runAutoEnhance">
					<i class="fas fa-magic"></i>
					<span>Auto</span>
				</button>
			</div>
		</div>

		<div ref="containerRef" class="cropper-area">
			<img
				ref="imageRef"
				:src="displayImageSrc"
				:style="{
					width: displaySize.width + 'px',
					height: displaySize.height + 'px',
					filter: filterCss
				}"
				class="cropper-image"
				@load="calculateDisplaySize"
			/>

			<svg
				class="cropper-overlay"
				:style="{
					left: `calc(50% - ${displaySize.width / 2}px)`,
					top: `calc(50% - ${displaySize.height / 2}px)`,
					width: displaySize.width + 'px',
					height: displaySize.height + 'px'
				}"
			>
				<defs>
					<mask id="cropMask">
						<rect width="100%" height="100%" fill="white"/>
						<path :d="cropPath" fill="black"/>
					</mask>
				</defs>
				<rect width="100%" height="100%" fill="rgba(0,0,0,0.6)" mask="url(#cropMask)"/>

				<path :d="cropPath" fill="none" stroke="#2080f0" stroke-width="2" stroke-dasharray="8,4"/>

				<line
					:x1="cornerPositions.topLeft.x + (cornerPositions.topRight.x - cornerPositions.topLeft.x) / 3"
					:y1="cornerPositions.topLeft.y + (cornerPositions.bottomLeft.y - cornerPositions.topLeft.y) / 3"
					:x2="cornerPositions.topRight.x + (cornerPositions.bottomRight.x - cornerPositions.topRight.x) / 3"
					:y2="cornerPositions.topRight.y + (cornerPositions.bottomRight.y - cornerPositions.topRight.y) / 3"
					stroke="rgba(255,255,255,0.3)" stroke-width="1"
				/>
				<line
					:x1="cornerPositions.topLeft.x + (cornerPositions.topRight.x - cornerPositions.topLeft.x) * 2 / 3"
					:y1="cornerPositions.topLeft.y + (cornerPositions.bottomLeft.y - cornerPositions.topLeft.y) * 2 / 3"
					:x2="cornerPositions.topRight.x + (cornerPositions.bottomRight.x - cornerPositions.topRight.x) * 2 / 3"
					:y2="cornerPositions.topRight.y + (cornerPositions.bottomRight.y - cornerPositions.topRight.y) * 2 / 3"
					stroke="rgba(255,255,255,0.3)" stroke-width="1"
				/>
			</svg>

			<div
				v-for="(position, corner) in cornerPositions"
				:key="corner"
				class="corner-handle"
				:style="{
					left: `calc(50% - ${displaySize.width / 2}px + ${position.x}px)`,
					top: `calc(50% - ${displaySize.height / 2}px + ${position.y}px)`
				}"
				@mousedown="handlePointerDown(corner, $event)"
				@touchstart="handlePointerDown(corner, $event)"
			>
				<div class="corner-dot" :class="{active: activeCorner === corner}"/>
				<svg class="corner-lines">
					<line x1="28" y1="28" :x2="corner.includes('Left') ? 10 : 46" y2="28" stroke="white" stroke-width="2"/>
					<line x1="28" y1="28" x2="28" :y2="corner.includes('top') ? 10 : 46" stroke="white" stroke-width="2"/>
				</svg>
			</div>

			<canvas
				v-show="isDragging && activeCorner"
				ref="magnifierCanvasRef"
				class="magnifier"
				:style="{
					width: MAGNIFIER_SIZE + 'px',
					height: MAGNIFIER_SIZE + 'px',
					...magnifierStyle
				}"
			/>
		</div>

		<div ref="footerRef" class="cropper-footer">
			<button @click="emit('cancel')" class="btn btn-light">
				Annuler
			</button>
			<button @click="applyCrop" class="btn btn-primary">
				<i class="fas fa-check me-2"></i>
				Valider
			</button>
		</div>

		<canvas ref="canvasRef" class="d-none"/>
	</div>
</template>

<style scoped>
.cropper-root {
	position: fixed;
	inset: 0;
	z-index: 2100;
	display: flex;
	flex-direction: column;
	background-color: #111827;
	/* 100dvh : sur mobile, inset:0 seul cale le bas du panneau sous la barre
	   d'outils du navigateur, et le footer (Annuler / Valider) devient
	   inatteignable. */
	height: 100vh;
	height: 100dvh;
	max-height: 100dvh;
	overflow: hidden;
}

.cropper-header {
	flex: 0 0 auto;
	background-color: #1f2937;
	padding: 0.75rem 1rem;
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.cropper-header h2 {
	color: #fff;
	font-size: 1.1rem;
	font-weight: 500;
	margin: 0;
}

.header-actions {
	display: flex;
	align-items: center;
	gap: 0.75rem;
}

.icon-btn {
	background: none;
	border: none;
	color: #9ca3af;
	padding: 0.4rem;
	font-size: 1.1rem;
	cursor: pointer;
}

.icon-btn:hover {
	color: #fff;
}

.reset-btn {
	background: none;
	border: none;
	color: #9ca3af;
	font-size: 0.9rem;
	cursor: pointer;
	display: flex;
	align-items: center;
}

.reset-btn:hover {
	color: #fff;
}

.cropper-instructions {
	flex: 0 0 auto;
	background-color: #2080f0;
	color: #fff;
	padding: 0.5rem 1rem;
	font-size: 0.9rem;
	text-align: center;
}

.icon-btn.active {
	color: #2080f0;
}

.adjust-panel {
	flex: 0 0 auto;
	background-color: #000;
	padding: 0.75rem 1rem;
	display: flex;
	flex-direction: column;
	gap: 0.85rem;
	border-bottom: 1px solid #262626;
}

.ruler-pill {
	display: flex;
	align-items: center;
	gap: 0.6rem;
	background-color: rgba(31, 41, 55, 0.85);
	border-radius: 999px;
	padding: 0.5rem 0.85rem;
}

.ruler-icon-sm, .ruler-icon-lg {
	color: #d1d5db;
	flex-shrink: 0;
}

.ruler-icon-sm {
	font-size: 0.75rem;
}

.ruler-icon-lg {
	font-size: 1.15rem;
}

.ruler-track {
	position: relative;
	flex: 1;
	height: 32px;
	touch-action: none;
	cursor: pointer;
	display: flex;
	align-items: center;
}

.ruler-ticks {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
}

.ruler-tick {
	width: 1px;
	height: 8px;
	background-color: rgba(255, 255, 255, 0.35);
	border-radius: 1px;
}

.ruler-tick.major {
	height: 14px;
	background-color: rgba(255, 255, 255, 0.55);
}

.ruler-thumb {
	position: absolute;
	top: 50%;
	width: 3px;
	height: 26px;
	background-color: #2080f0;
	border-radius: 2px;
	transform: translate(-50%, -50%);
	box-shadow: 0 0 6px rgba(32, 128, 240, 0.8);
	pointer-events: none;
}

.adjust-tabs {
	display: flex;
	align-items: center;
	justify-content: space-around;
	gap: 0.25rem;
}

.adjust-sep {
	width: 1px;
	height: 24px;
	background-color: #374151;
	flex-shrink: 0;
}

.adjust-tab {
	background: none;
	border: none;
	color: #9ca3af;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.3rem;
	font-size: 0.72rem;
	padding: 0.3rem 0.4rem;
	border-radius: 10px;
	flex: 1;
	min-width: 0;
}

.adjust-tab i {
	font-size: 1rem;
}

.adjust-tab span {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.adjust-tab.active {
	background-color: rgba(32, 128, 240, 0.18);
	color: #fff;
}

.adjust-tab.active i {
	color: #2080f0;
}

.cropper-area {
	position: relative;
	flex: 1 1 auto;
	/* Sans min-height:0, un flex item ne descend pas sous la taille de son
	   contenu : l'image poussait alors le footer hors de l'écran. */
	min-height: 0;
	overflow: hidden;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 1.5rem;
}

.cropper-image {
	max-width: 100%;
	max-height: 100%;
	object-fit: contain;
}

.cropper-overlay {
	position: absolute;
	pointer-events: none;
}

.corner-handle {
	position: absolute;
	width: 56px;
	height: 56px;
	margin-left: -28px;
	margin-top: -28px;
	touch-action: none;
}

.corner-dot {
	position: absolute;
	inset: 12px;
	border-radius: 50%;
	border: 4px solid #fff;
	background-color: #1868c1;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
	cursor: move;
	transition: transform 0.15s ease, background-color 0.15s ease;
}

.corner-dot.active {
	background-color: #2080f0;
	transform: scale(1.25);
}

.corner-lines {
	position: absolute;
	inset: 0;
	pointer-events: none;
}

.magnifier {
	position: absolute;
	pointer-events: none;
	z-index: 50;
	border-radius: 50%;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

.cropper-footer {
	flex: 0 0 auto;
	background-color: #fff;
	padding: 1rem;
	display: flex;
	gap: 0.75rem;
	padding-bottom: max(1rem, env(safe-area-inset-bottom));
}

.cropper-footer .btn {
	flex: 1;
	padding: 0.65rem 1rem;
}
</style>
